<script lang="ts" setup>
import type { FaqItem, OverviewActiveCampaign } from '~kui/types/learningCampaign';

import { Button } from '~ds/components/ui/button';
import { Text } from '~ds/components/ui/typography';
import { PrizeState } from '~kui/types/learningCampaign';
import { resolveCampaignPartnerAttribution } from '~kui/utils/campaignAttribution';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useI18n } from 'vue-i18n';

import KUIRewardsAlert from '../../Rewards/Alert/KUIRewardsAlert.vue';
import KUIUnifiedConfirmPrize from '../ConfirmPrize/KUIUnifiedConfirmPrize.vue';
import KUIUniPrizeOverviewTagRow from './KUIUniPrizeOverviewTagRow.vue';

interface Props {
  activeCampaign: OverviewActiveCampaign;
  isAuthenticated: boolean;
  hasCompletedAccount: boolean;
  termsTo: string;
  alertTitle: string;
  alertAriaLabel: string;
  alertDescription?: string;
  confirmPrizeLoading?: boolean;
  confirmPrizeError?: string | null;
  faqs?: FaqItem[];
}

const props = withDefaults(defineProps<Props>(), {
  alertDescription: '',
  confirmPrizeLoading: false,
  confirmPrizeError: null,
  faqs: () => [],
});

const emit = defineEmits<{
  enterLearningCampaign: [skillAnswer: string | null];
  verifyProfile: [];
}>();

// Draft answer to the campaign's game-of-skill question, captured in the confirm dialog.
const skillAnswer = ref('');

dayjs.extend(utc);

const { t } = useI18n();

const openConfirm = ref(false);
const loading = ref(false);

const handleOpenChange = (isOpen: boolean) => {
  if (!isOpen) {
    openConfirm.value = false;
  }
};

const handleCancel = () => {
  openConfirm.value = false;
  setTimeout(() => emit('verifyProfile'), 150);
};

watch(() => props.confirmPrizeLoading, (newVal, oldVal) => {
  loading.value = newVal;

  if (!newVal && oldVal && !props.confirmPrizeError) {
    openConfirm.value = false;
  }
}, { immediate: true });

const handleConfirmEntry = () => {
  loading.value = true;
  emit('enterLearningCampaign', props.activeCampaign.skillQuestion ? skillAnswer.value.trim() : null);
};

const hasPrize = computed(() => props.activeCampaign.hasPrize);

const canEnterPrize = computed(() =>
  hasPrize.value && props.isAuthenticated && !props.activeCampaign.currentUserLearningCampaignEntry,
);

const prizeState = computed(() => props.activeCampaign.prizeState ?? null);

const partnerAttributionLabel = computed(() => resolveCampaignPartnerAttribution({
  t,
  attributionLabel: props.activeCampaign.attributionLabel,
  hasPrize: hasPrize.value,
}));

const showAccountAlert = computed(() => canEnterPrize.value && prizeState.value === PrizeState.AccountIncomplete);
const showEnterButton = computed(() => canEnterPrize.value && prizeState.value === PrizeState.ReadyToEnter);

const campaignBlockedReason = computed(() => {
  if (!canEnterPrize.value || showEnterButton.value || showAccountAlert.value) {
    return null;
  }

  if (!props.activeCampaign.startDate || !props.activeCampaign.endDate) {
    return t('unified.campaigns.blockedReasons.campaignDatesNotValid');
  }

  if (dayjs.utc(props.activeCampaign.startDate).local().isAfter(dayjs())) {
    return t('unified.campaigns.blockedReasons.campaignNotStarted');
  }

  if (dayjs.utc(props.activeCampaign.endDate).local().isBefore(dayjs())) {
    return t('unified.campaigns.blockedReasons.campaignEnded');
  }

  return t('unified.campaigns.blockedReasons.completeExperienceRequired');
});
</script>

<template>
  <div class="flex flex-col gap-3 mt-10 relative">
    <KUIUniPrizeOverviewTagRow
      v-if="activeCampaign.currentUserLearningCampaignEntry && isAuthenticated"
      :has-completed-account="hasCompletedAccount"
      @verify-profile="$emit('verifyProfile')"
    />

    <div class="flex flex-col mb-5">
      <div class="flex flex-col mb-6">
        <KUIUnifiedViewHeader
          :title="activeCampaign.name"
          semantic-size="h2"
          size="2xl"
          class="mt-0"
        />
      </div>

      <div
        v-if="activeCampaign.campaignDescription"
        class="flex items-center gap-2 mb-6"
      >
        <Text>{{ activeCampaign.campaignDescription }}</Text>
      </div>

      <div v-if="activeCampaign.campaignPartnerLogo" class="flex items-center gap-2 mb-8.5">
        <Text size="sm" class="text-foreground">
          {{ partnerAttributionLabel }}
        </Text>
        <NuxtImg
          :src="activeCampaign.campaignPartnerLogo.src"
          alt=""
          role="presentation"
          class="max-h-10 w-auto"
        />
      </div>
      <div class="flex items-center justify-center mb-8.5">
        <NuxtImg
          v-if="activeCampaign.heroImageUrl"
          :src="activeCampaign.heroImageUrl"
          :alt="activeCampaign.name"
          class="w-full rounded-2xl object-cover h-64 select-none pointer-events-none"
        />
      </div>
      <div class="mx-auto w-full max-w-2xl shrink-0 flex flex-col space-y-6 text-center mt-4">
        <KUIRewardsAlert
          v-if="showAccountAlert"
          :title="alertTitle"
          :description="alertDescription"
          :aria-label="alertAriaLabel"
          @click="$emit('enterLearningCampaign', null)"
        />
        <KUICoreAlertMessageText
          v-if="campaignBlockedReason"
          :message="campaignBlockedReason"
          variant="error"
        />
        <Button
          v-if="showEnterButton"
          variant="outline"
          size="lg"
          :loading="loading"
          class="w-full"
          type="button"
          @click="openConfirm = true"
        >
          {{ t('unified.activity.completion.enterPrizeDraw') }}
        </Button>

        <NuxtLink
          v-if="hasPrize"
          :to="termsTo"
          class="underline font-semibold mt-2 text-xs"
          external
          target="_blank"
        >
          {{ t('unified.campaigns.termsAndConditions.heading') }}
        </NuxtLink>
      </div>
    </div>

    <div v-if="faqs.length > 0" class="my-10">
      <KUICoreFaqSection
        :title="t('learn.faqs')"
        :faqs="faqs"
        heading-align="center"
        is-rich-text
      />
    </div>

    <KUIUnifiedConfirmPrize
      v-if="showEnterButton"
      v-model:skill-answer="skillAnswer"
      :open="openConfirm"
      :prize-name="activeCampaign.name"
      :terms-to="termsTo"
      :loading="confirmPrizeLoading"
      :error="confirmPrizeError"
      :skill-question="activeCampaign.skillQuestion ?? null"
      @confirm-entry="handleConfirmEntry"
      @cancel="handleCancel"
      @update:open="handleOpenChange"
    />
  </div>
</template>
